import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listCustomers, type Customer } from "../api/admin";
import Icon from "../components/icons";

const trLower = (s: string) => s.toLocaleLowerCase("tr-TR");
// Binlik ayraç zorunlu: araç çubuğundaki toplam zaten "121.002" yazıyordu, satırlar
// çıplak "101000" gösteriyordu. Muhasebe ekranında rakam tek bakışta okunmalı.
const sayi = (n: number) => n.toLocaleString("tr-TR");

export default function Accounting() {
  const [rows, setRows] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        setRows(await listCustomers());
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "Yükleme hatası");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const withCredit = useMemo(() => rows.filter((c) => c.credit_balance > 0), [rows]);
  const filtered = useMemo(() => {
    const needle = trLower(q.trim());
    if (!needle) return withCredit;
    return withCredit.filter((c) => trLower(c.name).includes(needle) || trLower(c.email || "").includes(needle));
  }, [withCredit, q]);

  // Toplam bakiye: "kaç müşteri" kadar "ne kadar kredi dışarıda" da bilinmeli.
  const toplam = useMemo(() => filtered.reduce((n, c) => n + c.credit_balance, 0), [filtered]);

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-row">
          <h1>Muhasebe</h1>
          {/* "3 krediye sahip müşteri" iki türlü okunuyordu (3 kredisi olan müşteri /
              krediye sahip 3 müşteri). Sayı öne alınmadı, niteleme öne alındı. */}
          <span className="muted">Krediye sahip {withCredit.length} müşteri</span>
        </div>
        <p className="page-sub">Doküman kredisi olan müşteriler ve bakiyeleri.</p>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        <div className="data-table-toolbar">
          <label className="data-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              placeholder="Ad veya e-posta ara…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Muhasebe kayıtlarında ara"
            />
          </label>
          <span className="data-count">
            {filtered.length} müşteri · toplam {sayi(toplam)} kredi
          </span>
        </div>

        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : withCredit.length === 0 ? (
          <div className="empty-state">
            <Icon name="inbox" size={34} />
            <p>Krediye sahip müşteri yok.</p>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Kredi, müşteri detayından tanımlanır veya satın alma ile yüklenir.
            </span>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="dt-num">ID</th>
                  <th>Ad</th>
                  <th>Tip</th>
                  <th>E-posta</th>
                  <th className="dt-num">Kredi</th>
                  <th className="dt-num">Üretilen GSPR</th>
                  <th style={{ textAlign: "right" }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="dt-empty">“{q}” ile eşleşen müşteri yok.</td></tr>
                ) : filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="dt-num dt-muted">{c.id}</td>
                    <td>
                      <div className="dt-main">
                        <span className="dt-avatar">{(c.name || "?").charAt(0).toLocaleUpperCase("tr-TR")}</span>
                        <span className="dt-name" title={c.name}>{c.name}</span>
                      </div>
                    </td>
                    <td><span className="badge neutral">{c.type === "kurumsal" ? "Kurumsal" : "Bireysel"}</span></td>
                    <td className="dt-muted">{c.email || "—"}</td>
                    <td className="dt-num"><span className="badge ok">{sayi(c.credit_balance)}</span></td>
                    {/* Sayaç rozetsiz: rozetin 8px iç boşluğu rakamı sola itiyor,
                        "10" ile "9" aynı sütunda farklı yerde duruyordu (ui-admin §4:
                        sayısal sütunda rakamlar dikey hizalanır). */}
                    <td className="dt-num">
                      {c.gspr_count > 0 ? sayi(c.gspr_count) : <span className="dt-muted">—</span>}
                    </td>
                    <td>
                      <div className="dt-actions">
                        <Link
                          className="dt-icon-btn"
                          to={`/customers/${c.id}`}
                          title={`${c.name} — detay`}
                          aria-label={`${c.name} müşterisinin detayını aç`}
                        >
                          <Icon name="eye" size={16} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
